import { useCallback, useMemo, useRef } from 'react';
import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { mapServerValidationErrors, type ServerFieldMap } from '@/shared/forms';
import {
  useCreateProductsPlaceholder,
  useUpdateProductsPlaceholder,
} from '@/api/generated/products-placeholders/products-placeholders';
import {
  buildPlaceholderFormSchema,
  emptyPlaceholderFormValues,
  type PlaceholderFormValues,
} from './placeholderFormSchema';
import { toPlaceholderRequestBody } from './toPlaceholderRequest';

/** Mapování názvů polí z BE validace na cesty polí ve formuláři. */
const SERVER_FIELD_MAP: ServerFieldMap = {
  Name: 'name',
  SubcategoryId: 'subcategory',
  FacilitySubTypeId: 'facilitySubType',
  AccommodationCategoryCbId: 'accommodationCategory',
  TransportKind: 'transportKind',
};

interface UsePlaceholderFormOptions {
  /** Bez `placeholderId` = zakládání, s `placeholderId` = editace. */
  placeholderId?: string;
  initialValues?: PlaceholderFormValues;
  /** Voláno po úspěšném uložení (předává id uloženého placeholderu) — invalidace, toast; NEzavírá okno. */
  onSaved: (id: string) => void;
  /** Voláno při chybě, kterou se nepodařilo namapovat na pole. */
  onError?: (error: unknown) => void;
}

/**
 * Formulářová logika placeholderu (sdílená pro zakládání i editaci) — TanStack Form se Zod validací,
 * create/update mutace a promítnutí serverových validačních chyb do polí. `submit()` vrací, zda se
 * uložení povedlo (pro „Uložit & zpět").
 */
export function usePlaceholderForm({
  placeholderId,
  initialValues,
  onSaved,
  onError,
}: UsePlaceholderFormOptions) {
  const { t } = useTranslation('venua-planner');
  const schema = useMemo(() => buildPlaceholderFormSchema(t), [t]);

  const createMutation = useCreateProductsPlaceholder();
  const updateMutation = useUpdateProductsPlaceholder();
  const successRef = useRef(false);

  const form = useForm({
    defaultValues: initialValues ?? emptyPlaceholderFormValues(),
    validators: { onChange: schema },
    onSubmit: async ({ value, formApi }) => {
      successRef.current = false;
      const data = toPlaceholderRequestBody(value);
      try {
        if (placeholderId != null) {
          await updateMutation.mutateAsync({ id: placeholderId, data });
          onSaved(placeholderId);
        } else {
          const newId = await createMutation.mutateAsync({ data });
          onSaved(newId);
        }
        successRef.current = true;
      } catch (error) {
        const mapped = mapServerValidationErrors(formApi, error, SERVER_FIELD_MAP);
        if (!mapped) onError?.(error);
      }
    },
  });

  /** Spustí validaci + uložení; vrací `true` při úspěchu. */
  const submit = useCallback(async () => {
    await form.handleSubmit();
    return successRef.current;
  }, [form]);

  return {
    form,
    submit,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
  };
}

/** Typ instance TanStack Form pro detail placeholderu (pro propsy podřízených komponent). */
export type PlaceholderFormApi = ReturnType<typeof usePlaceholderForm>['form'];
